import {Component} from "react"
import posthog from "posthog-js"
import {createPortal} from "react-dom"
import LoginHelper from "src/components/login/LoginHelper"
import navigationService from "src/navigation/NavigationService"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"

interface AccountPanelProps {
    isOpen: boolean;
    onClose: () => void;
    apiHandler: any;
    loginHelper?: LoginHelper;
    stateHandler?: EventAdaptingStateHandler;
}

interface AccountPanelState {
    userEmail: string | null;
    isLoading: boolean;
}

class AccountPanel extends Component<AccountPanelProps, AccountPanelState> {
    constructor(props: AccountPanelProps) {
        super(props);
        this.state = {
            userEmail: null,
            isLoading: false
        };
    }

    async componentDidMount() {
        if (this.props.isOpen) {
            await this.fetchUserInfo();
        }
    }

    async componentDidUpdate(prevProps: AccountPanelProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            await this.fetchUserInfo();
        }
    }

    async fetchUserInfo() {
        if (!this.props.apiHandler) return;
        
        this.setState({ isLoading: true });
        try {
            const userInfo = await this.props.apiHandler.getUserInfo();
            const features: string[] = userInfo.features || []
            if (this.props.stateHandler) {
                this.props.stateHandler.set(StateConstants.USER_FEATURES, features)
                this.props.stateHandler.set(StateConstants.ACCOUNT_TYPE, userInfo.at || "basic")
                this.props.stateHandler.set(StateConstants.IS_PREMIUM, features.length > 0)
            }
            this.setState({
                userEmail: userInfo.e || null,
                isLoading: false
            });
        } catch (error) {
            console.error('Failed to fetch user info:', error);
            this.setState({ 
                userEmail: null,
                isLoading: false 
            });
        }
    }

    async handleLogout() {
        if (this.props.loginHelper) {
            await this.props.loginHelper.logout();
        }
        if (this.props.apiHandler) {
            this.props.apiHandler.clearCache();
        }

        this.props.onClose();
    }

    render() {
        if (!this.props.isOpen) return null;

        return createPortal(
            <>
                {/* Backdrop */}
                <div 
                    className="account-panel-backdrop" 
                    onClick={this.props.onClose}
                ></div>
                
                {/* Panel */}
                <div className="account-panel">
                    <div className="account-panel-header">
                        <h3>Account</h3>
                        <button 
                            className="account-panel-close" 
                            onClick={this.props.onClose}
                        >
                            ×
                        </button>
                    </div>
                    
                    <div className="account-panel-content">
                        {this.props.stateHandler?.get(StateConstants.IS_PREMIUM) ? (
                            <div className="account-tier-badge account-tier-lifetime">
                                Lifetime Member
                            </div>
                        ) : (
                            <div className="account-tier-badge account-tier-free">
                                Free Plan
                            </div>
                        )}

                        <div className="account-info">
                            <div className="account-avatar">
                                <img src="/homeindex_logo.svg" alt="Account" style={{width: 24, height: 24}} />
                            </div>
                            <div className="account-details">
                                {this.state.userEmail ? (
                                    <p>{this.state.userEmail}</p>
                                ) : (
                                    <p>Manage your account</p>
                                )}
                            </div>
                        </div>

                        {!this.props.stateHandler?.get(StateConstants.IS_PREMIUM) && (
                            <button
                                className="account-upgrade-button"
                                onClick={() => {
                                    posthog?.capture("plans_opened", { source: "account_panel" })
                                    this.props.onClose();
                                    this.props.stateHandler?.set(StateConstants.SHOW_PLAN_MODAL, true);
                                }}
                            >
                                Upgrade to Lifetime
                            </button>
                        )}

                        <div className="account-panel-footer">
                            <button
                                className="logout-button"
                                onClick={() => this.handleLogout()}
                            >
                                Log Out
                            </button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        );
    }
}

export default AccountPanel
